/**
 * Générateur PDF rapport : Ultra Mesure
 *
 * Orchestre l'assemblage du document pdfmake :
 * couverture → mission → méthodologie → mesures (par zone) → conclusions → synthèse → signature
 *
 * Utilise un import dynamique de pdfmake pour ne pas alourdir le bundle.
 */

import type { Content, TDocumentDefinitions } from "pdfmake/interfaces";
import { offlineDb } from "@/core/offline/db";
import type { OfflineRapport, CustomSection } from "@/core/offline/db";
import { fetchSiteExportData, fetchPreviousCampagneMesures } from "@/modules/corrosion/export/data-fetcher";
import type { PinExportData, PreviousCampagneData, ZoneExportData } from "@/modules/corrosion/export/data-fetcher";
import { generateAnnotatedPlanBlob } from "@/modules/corrosion/export/plan-export";
import { tiptapToPdfmake } from "./tiptap-to-pdfmake";
import { computeRapportVariables } from "./pdf-variables";
import { computeNetworkMaterialStats } from "./network-material-stats";
import {
  buildCoverPage,
  buildSectionTitle,
  buildMesureFiche,
  buildZoneTitle,
  buildPlanImage,
  buildPlanLegend,
  buildSynthesisTable,
  buildSignatureBlock,
  buildPhotoAnalysisSection,
  type PhotoRenderInfo,
} from "./pdf-sections";
import { PDF_COLORS, PDF_FONT_SIZES, PDF_MARGINS, PDF_STYLES } from "./pdf-styles";

// ─── API publique ────────────────────────────────────────────

/**
 * Génère le PDF complet pour un site et retourne un Blob.
 * Tout est chargé depuis Dexie (offline).
 *
 * @param siteId      Site cible
 * @param campagneId  Campagne à figer dans le PDF (défaut : active ou la plus récente)
 */
export async function generateRapportPdf(
  siteId: string,
  campagneId?: string
): Promise<Blob> {
  // 1. Charger les données (snapshot de la campagne demandée ou de la défaut)
  const data = await fetchSiteExportData(siteId, campagneId);
  if (!data) throw new Error("Site introuvable");

  const rapport = await offlineDb.rapports.where("siteId").equals(siteId).first();
  if (!rapport) throw new Error("Rapport introuvable pour ce site");

  // 1b. Calculer les variables dynamiques ({{site}}, {{min}}, {{max}}, etc.)
  const variables = computeRapportVariables(data);
  // Stats réseau x matériau pour la variable rédactionnelle {{mesures_min_max_moy}}
  const stats = computeNetworkMaterialStats(data);

  // 2. Charger les logos et la signature en base64
  const logoCoverDataUrl = await loadImageAsDataUrl("/logo-ultramesure-cover.jpg"); // Couverture : goutte + texte
  const logoHeaderDataUrl = await loadImageAsDataUrl("/logo-ultramesure-edition.png"); // Header pages : texte seul
  const signatureDataUrl = await loadImageAsDataUrl("/signature-ultramesure.png"); // Bloc signature + tampon

  // 3. Générer les plans annotés (1 par zone)
  const planDataUrls = new Map<string, string>();
  for (const zoneData of data.zones) {
    try {
      const planBlob = await generateAnnotatedPlanBlob(zoneData);
      if (planBlob) {
        planDataUrls.set(zoneData.zone.id, await blobToDataUrl(planBlob));
      } else {
        console.warn(`[PDF] Plan null pour "${zoneData.zone.name}"`);
      }
    } catch (e) {
      console.error(`[PDF] Erreur plan "${zoneData.zone.name}":`, e);
    }
  }
  // 4. Convertir les photos en base64 (toutes les photos de toutes les mesures)
  // Si le blob n'est pas en IndexedDB, tenter de le télécharger depuis le serveur.
  // On détecte aussi l'orientation (portrait vs paysage) pour adapter le layout PDF :
  // photos portrait → 3 fiches/page, paysage → 2 fiches/page.
  const photoDataUrls = new Map<string, PhotoRenderInfo>();
  const token = localStorage.getItem("ultra-mesure-token");
  for (const zoneData of data.zones) {
    for (const pinData of zoneData.pins) {
      for (const photo of pinData.photos) {
        try {
          let blob: Blob | null = photo.blob;
          // Fallback serveur si pas de blob local
          if ((!blob || blob.size === 0) && photo.serverUrl) {
            try {
              const headers: Record<string, string> = {};
              if (token) headers["Authorization"] = `Bearer ${token}`;
              const resp = await fetch(photo.serverUrl, { headers });
              if (resp.ok) blob = await resp.blob();
            } catch { /* serveur indisponible */ }
          }
          if (blob && blob.size > 0) {
            const dataUrl = await blobToDataUrl(blob);
            const isPortrait = await detectPortrait(dataUrl);
            photoDataUrls.set(photo.id, { dataUrl, isPortrait });
          }
        } catch {
          // Photo corrompue ou blob invalide → on continue sans
        }
      }
    }
  }

  // 4b. Charger la campagne precedente (N-X) pour comparatif dans le tableau de synthese
  const previousData = await fetchPreviousCampagneMesures(siteId, data.campagne);

  // 5. Construire le document
  const docContent = await buildDocumentContent(data.site, rapport, data.zones, logoCoverDataUrl, planDataUrls, photoDataUrls, signatureDataUrl, variables, stats, previousData, data.campagne.date);

  // 6. Générer le PDF via pdfmake (import dynamique)
  const blob = await renderPdf(docContent, rapport, data.site, logoHeaderDataUrl);
  return blob;
}

// ─── Construction du contenu ─────────────────────────────────

async function buildDocumentContent(
  site: import("@/core/offline/db").OfflineSite,
  rapport: OfflineRapport,
  zones: ZoneExportData[],
  logoDataUrl: string | undefined,
  planDataUrls: Map<string, string>,
  photoDataUrls: Map<string, PhotoRenderInfo>,
  signatureDataUrl: string | undefined,
  variables: import("./pdf-variables").RapportVariables,
  stats: import("./network-material-stats").ReseauStatGroup[],
  previousData: PreviousCampagneData | null,
  campagneDate: Date
): Promise<Content[]> {
  const content: Content[] = [];

  const customSections: CustomSection[] = rapport.customSections ?? [];

  // Helper : insère les sections libres après une section fixe donnée
  const insertCustomAfter = async (afterId: string, sectionNum: { value: number }) => {
    const matching = customSections
      .filter((s) => s.afterSection === afterId)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    for (const cs of matching) {
      sectionNum.value++;

      // Toute section libre démarre sur une nouvelle page
      content.push({ text: "", pageBreak: "before" });

      if (cs.type === "photo-analysis") {
        // Convertir le blob photo en dataUrl si présent
        let photoDataUrl: string | undefined;
        if (cs.photoBlob && cs.photoBlob instanceof Blob && cs.photoBlob.size > 0) {
          try {
            photoDataUrl = await blobToDataUrl(cs.photoBlob);
          } catch { /* photo corrompue */ }
        }
        content.push(...buildPhotoAnalysisSection(
          cs.title || "Photo et analyse",
          photoDataUrl,
          cs.content,
          sectionNum.value
        ));
      } else if (cs.type === "pdf-annexe" && cs.pdfAnnexeId) {
        // Sentinelle pour fusion pdf-lib post-render.
        // Le titre n'est PAS rendu via pdfmake : il est dessiné en overlay
        // (style bandeau de section) sur la première page de l'annexe.
        content.push({
          _pdfAnnexeMarker: cs.pdfAnnexeId,
          _pdfAnnexeTitle: cs.title || "Annexe",
          _pdfAnnexeNum: sectionNum.value,
        } as unknown as Content);
      } else {
        content.push(buildSectionTitle(sectionNum.value, cs.title || "Section libre"));
        const csContent = tiptapToPdfmake(cs.content, variables, stats);
        if (csContent.length > 0) {
          content.push(...csContent);
        } else {
          content.push({ text: "(Aucun contenu)", fontSize: PDF_FONT_SIZES.body, color: PDF_COLORS.textLight, italics: true });
        }
      }
    }
  };

  // Compteur de numérotation dynamique (objet mutable pour passer par ref)
  const sectionNum = { value: 0 };

  // Sections fixes masquées par l'utilisateur (cf. rapport-edit.tsx).
  // On saute ENTIÈREMENT le titre + contenu pour rester cohérent avec l'éditeur.
  // Les customs rattachées via afterSection="X" sont quand même rendues à leur place.
  const hiddenFixed = new Set<string>(
    Array.isArray(rapport.hiddenSections) ? rapport.hiddenSections : []
  );
  const isHidden = (key: string) => hiddenFixed.has(key);

  // ═══ COUVERTURE ═══
  if (!isHidden("couverture")) content.push(...buildCoverPage(rapport, site, logoDataUrl));
  await insertCustomAfter("couverture", sectionNum);

  // ═══ SECTION : MISSION ═══
  if (!isHidden("mission")) {
    sectionNum.value++;
    content.push(buildSectionTitle(sectionNum.value, "Mission"));
    const missionContent = tiptapToPdfmake(rapport.missionText, variables, stats);
    if (missionContent.length > 0) {
      content.push(...missionContent);
    } else {
      content.push({ text: "(Aucun contenu)", fontSize: PDF_FONT_SIZES.body, color: PDF_COLORS.textLight, italics: true });
    }
  }
  await insertCustomAfter("mission", sectionNum);

  // ═══ SECTION : MÉTHODOLOGIE ═══
  if (!isHidden("methodologie")) {
    sectionNum.value++;
    content.push(buildSectionTitle(sectionNum.value, "M\u00e9thodologie"));
    const methodoContent = tiptapToPdfmake(rapport.methodologieText, variables, stats);
    if (methodoContent.length > 0) {
      content.push(...methodoContent);
    } else {
      content.push({ text: "(Aucun contenu)", fontSize: PDF_FONT_SIZES.body, color: PDF_COLORS.textLight, italics: true });
    }
  }
  await insertCustomAfter("methodologie", sectionNum);

  // ═══ SECTION : MESURES ═══
  // Collecte de tous les pins par zone pour le tableau de synthèse
  const allPins: PinExportData[] = [];
  const pinsByZone: Array<{ zoneName: string; pins: PinExportData[] }> = [];

  const showMesures = !isHidden("mesures");
  for (let zi = 0; zi < zones.length && showMesures; zi++) {
    const zoneData = zones[zi];

    // --- Page 1 de la zone : titre section (1ère zone seulement) + titre zone + plan ---
    content.push({ text: "", pageBreak: "before" });
    if (zi === 0) {
      sectionNum.value++;
      content.push(buildSectionTitle(sectionNum.value, "R\u00e9sultats des mesures"));
    }
    // Plan annoté (adapté pour tenir sur la page avec les titres) + légende fixe.
    const planUrl = planDataUrls.get(zoneData.zone.id);
    const zonePins: PinExportData[] = [...zoneData.pins].sort(
      (a, b) => a.pin.number - b.pin.number
    );

    if (planUrl) {
      // Zone AVEC plan : titre zone + plan annoté + légende, puis page des mesures à part.
      content.push(buildZoneTitle(zoneData.zone.name));
      content.push(buildPlanImage(planUrl));
      content.push(buildPlanLegend());
      if (zonePins.length > 0) {
        content.push({ text: "", pageBreak: "before" });
        // Rappel du nom de zone en haut de la page des mesures
        content.push(buildZoneTitle(zoneData.zone.name));
      }
    } else {
      // Zone SANS plan : pas de page de plan vide, on enchaîne sur les mesures.
      content.push(buildZoneTitle(zoneData.zone.name));
    }

    if (zonePins.length > 0) {
      const zoneGroup: PinExportData[] = [];
      for (const pinData of zonePins) {
        allPins.push(pinData);
        zoneGroup.push(pinData);
        const ficheContent = buildMesureFiche(pinData, photoDataUrls);
        content.push(...ficheContent);
      }
      pinsByZone.push({ zoneName: zoneData.zone.name, pins: zoneGroup });
    }
  }

  await insertCustomAfter("mesures", sectionNum);

  // ═══ SECTION : CONCLUSIONS ═══
  if (!isHidden("conclusions")) {
    sectionNum.value++;
    content.push({ text: "", pageBreak: "before" });
    content.push(buildSectionTitle(sectionNum.value, "Conclusions"));
    const conclusionsContent = tiptapToPdfmake(rapport.conclusionsText, variables, stats);
    if (conclusionsContent.length > 0) {
      content.push(...conclusionsContent);
    } else {
      content.push({ text: "(Aucun contenu)", fontSize: PDF_FONT_SIZES.body, color: PDF_COLORS.textLight, italics: true });
    }
  }
  await insertCustomAfter("conclusions", sectionNum);

  // ═══ SIGNATURE + NOTA BENE ═══
  // Format Renaud : signature + NB inline à la fin des conclusions (pas de
  // pageBreak), puis tableau de synthèse sur une nouvelle page APRÈS.
  content.push(...buildSignatureBlock(rapport, signatureDataUrl));

  // ═══ SECTION : TABLEAU DE SYNTHÈSE ═══
  if (allPins.length > 0 && !isHidden("synthese")) {
    sectionNum.value++;
    content.push({ text: "", pageBreak: "before" });
    content.push(buildSectionTitle(sectionNum.value, "Tableau de synth\u00e8se"));
    // Si une campagne de reference existe, on passe les options au tableau
    // pour afficher les 2 colonnes "% N-X" + "Evolution".
    const synthOptions = previousData
      ? {
          previous: {
            previousMesureByPinId: previousData.mesureByPinId,
            previousLabel: formatCampagneShortLabel(previousData.campagne.date),
          },
          mesureDate: campagneDate,
        }
      : { mesureDate: campagneDate };
    content.push(...buildSynthesisTable(pinsByZone, site, synthOptions));
  }

  return content;
}

// ─── Rendu pdfmake ───────────────────────────────────────────

/**
 * Construit la TDocumentDefinition pdfmake pour un segment de contenu.
 *
 * pageOffset     : pages globales qui précèdent ce segment (0 pour le 1er).
 * totalPages     : total de pages du document fusionné final.
 * nativePageCount: si true, le footer utilise le pageCount pdfmake (fast-path sans annexes).
 */
function buildDocDef(
  segContent: Content[],
  rapport: OfflineRapport,
  site: import("@/core/offline/db").OfflineSite,
  logoDataUrl: string | undefined,
  pageOffset: number,
  totalPages: number,
  nativePageCount: boolean
): TDocumentDefinitions {
  return {
    pageSize: "A4",
    pageMargins: PDF_MARGINS,
    info: {
      title: `Rapport ${rapport.numero ?? ""} : ${site.name}`,
      author: rapport.signataireNom ?? "Ultramesure",
      subject: "Rapport de diagnostic corrosion",
    },
    header: (currentPage: number) => {
      if (pageOffset === 0 && currentPage === 1) return null;
      return {
        columns: [
          {
            text: [
              { text: `Affaire ${rapport.numero ?? ""}`, color: PDF_COLORS.blue, bold: true },
              { text: ` \u2014 ${site.name}`, color: PDF_COLORS.blue, bold: true },
            ],
            fontSize: PDF_FONT_SIZES.headerFooter,
          },
          ...(logoDataUrl
            ? [{ image: logoDataUrl, height: 20, alignment: "right" as const }]
            : []),
        ],
        margin: [40, 20, 40, 0] as [number, number, number, number],
      };
    },
    footer: (currentPage: number, pageCount: number) => {
      if (pageOffset === 0 && currentPage === 1) return null;
      const effective = currentPage + pageOffset;
      const total = nativePageCount ? pageCount : totalPages;
      return {
        text: `${effective} / ${total}`,
        fontSize: PDF_FONT_SIZES.headerFooter,
        color: PDF_COLORS.textLight,
        alignment: "right",
        margin: [0, 0, 40, 20] as [number, number, number, number],
      };
    },
    content: segContent,
    styles: PDF_STYLES,
    defaultStyle: {
      fontSize: PDF_FONT_SIZES.body,
      color: PDF_COLORS.text,
    },
  };
}

async function renderPdf(
  content: Content[],
  rapport: OfflineRapport,
  site: import("@/core/offline/db").OfflineSite,
  logoDataUrl: string | undefined
): Promise<Blob> {
  // --- Setup pdfmake (import dynamique + VFS fonts) ---
  const pdfMakeModule = await import("pdfmake/build/pdfmake");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pdfMake = (pdfMakeModule.default ?? pdfMakeModule) as any;
  const vfsFontsModule = await import("pdfmake/build/vfs_fonts") as Record<string, unknown>;
  const vfsObj: Record<string, string> = {};
  for (const [key, val] of Object.entries(vfsFontsModule)) {
    if (key === "default" || key === "__esModule") continue;
    if (typeof val === "string") vfsObj[key] = val;
  }
  if (Object.keys(vfsObj).length === 0) {
    const def = vfsFontsModule.default as Record<string, unknown> | undefined;
    if (def) {
      const pdfMakeObj = def.pdfMake as Record<string, unknown> | undefined;
      const vfsCandidate = pdfMakeObj?.vfs ?? def.vfs ?? def;
      if (typeof vfsCandidate === "object" && vfsCandidate !== null) Object.assign(vfsObj, vfsCandidate);
    }
  }
  pdfMake.vfs = vfsObj;

  // --- Découper le content au niveau des sentinelles _pdfAnnexeMarker ---
  // contentSegments[i] = bloc pdfmake ; segmentAnnexeIds[i] = annexe inline qui suit (ou null)
  const contentSegments: Content[][] = [];
  const segmentAnnexeIds: (string | null)[] = [];
  const inlineIds = new Set<string>();
  // Titre + numéro de section de chaque annexe inline (pour l'overlay du bandeau)
  const annexeTitles = new Map<string, { num: number; title: string }>();
  let cur: Content[] = [];

  for (const item of content) {
    const rec = item as unknown as Record<string, unknown>;
    const marker = rec._pdfAnnexeMarker as string | undefined;
    if (marker) {
      contentSegments.push(cur);
      segmentAnnexeIds.push(marker);
      inlineIds.add(marker);
      annexeTitles.set(marker, {
        num: (rec._pdfAnnexeNum as number) ?? 0,
        title: (rec._pdfAnnexeTitle as string) ?? "Annexe",
      });
      cur = [];
    } else {
      cur.push(item);
    }
  }
  contentSegments.push(cur);
  segmentAnnexeIds.push(null);

  // --- Charger toutes les annexes du rapport ---
  const token = localStorage.getItem("ultra-mesure-token");
  const allAnnexes = await offlineDb.rapportAnnexes.where("rapportId").equals(rapport.id).toArray();
  allAnnexes.sort((a, b) => a.order - b.order);
  const orphanAnnexes = allAnnexes.filter((a) => !inlineIds.has(a.id));

  // Fast-path : aucune annexe → rendu simple, pdfmake gère la pagination nativement
  if (allAnnexes.length === 0 && inlineIds.size === 0) {
    const docDef = buildDocDef(content, rapport, site, logoDataUrl, 0, 0, true);
    return await pdfMake.createPdf(docDef).getBlob();
  }

  // --- Pré-charger les bytes de toutes les annexes (évite un double fetch en 2 passes) ---
  const { PDFDocument } = await import("pdf-lib");
  const annexeBytes = new Map<string, ArrayBuffer>();
  for (const annexe of allAnnexes) {
    try {
      let bytes: ArrayBuffer | null = null;
      if (annexe.blob && annexe.blob.size > 0) {
        bytes = await annexe.blob.arrayBuffer();
      } else if (annexe.serverUrl) {
        const headers: Record<string, string> = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;
        const resp = await fetch(annexe.serverUrl, { headers });
        if (resp.ok) bytes = await resp.arrayBuffer();
      }
      if (bytes) annexeBytes.set(annexe.id, bytes);
      else console.warn(`[PDF] Annexe "${annexe.fileName}" introuvable, ignorée`);
    } catch (e) {
      console.error(`[PDF] Chargement annexe "${annexe.fileName}":`, e);
    }
  }

  // --- Nettoyage des frontières de segments autour des annexes ---
  // Chaque section custom est précédée d'un `{text:"", pageBreak:"before"}`.
  // Une fois le content découpé au niveau des sentinelles, ce saut se retrouve
  // en FIN du segment précédent (page vierge AVANT l'annexe) et le saut de la
  // section suivante en DÉBUT du segment d'après (page vierge APRÈS l'annexe).
  // L'annexe occupant ses propres pages, ces sauts sont inutiles : on les
  // retire, mais uniquement si l'annexe a bien été chargée (sinon on garde la
  // séparation entre les sections qui se retrouvent adjacentes).
  const isPureBreak = (item: Content | undefined): boolean => {
    if (!item || typeof item !== "object") return false;
    const rec = item as unknown as Record<string, unknown>;
    return rec.text === "" && typeof rec.pageBreak === "string";
  };
  for (let i = 0; i < segmentAnnexeIds.length; i++) {
    const id = segmentAnnexeIds[i];
    if (!id || !annexeBytes.has(id)) continue;
    const before = contentSegments[i];
    if (isPureBreak(before[before.length - 1])) before.pop();
    const after = contentSegments[i + 1];
    if (after && isPureBreak(after[0])) after.shift();
  }

  // --- Helper : rendre un segment pdfmake en Blob ---
  // ⚠️ pdfmake MUTE les objets passés (images remplacées par des refs internes
  // `$$pdfmake$$N` qui pointent vers son VFS local). Si on rend 2× le même
  // content array, le 2ème render reçoit ces refs cassées → erreur
  // "File '$$pdfmake$$1' not found". On clone donc en profondeur à chaque appel.
  // structuredClone ne marche pas : le content peut contenir des fonctions
  // (largeurs de cellules, layouts custom) qui ne sont pas clonables.
  // On fait donc un deep clone récursif qui préserve les fonctions par référence.
  const renderSeg = async (seg: Content[], pgOffset: number, totalPg: number): Promise<Blob> => {
    const segClone = deepCloneKeepFns(seg) as Content[];
    const docDef = buildDocDef(segClone, rapport, site, logoDataUrl, pgOffset, totalPg, false);
    return await pdfMake.createPdf(docDef).getBlob();
  };

  // --- Helper : compter les pages d'un PDF (ArrayBuffer) ---
  const countPages = async (bytes: ArrayBuffer): Promise<number> => {
    const doc = await PDFDocument.load(bytes);
    return doc.getPageCount();
  };

  // --- Pass 1 : rendre chaque segment (offset=0) pour compter ses pages ---
  const segPageCounts: number[] = [];
  for (let i = 0; i < contentSegments.length; i++) {
    if (contentSegments[i].length === 0) { segPageCounts.push(0); continue; }
    const blob = await renderSeg(contentSegments[i], 0, 1);
    segPageCounts.push(await countPages(await blob.arrayBuffer()));
  }

  // Compter les pages des annexes inline
  const inlinePageCounts = new Map<string, number>();
  for (const id of inlineIds) {
    const bytes = annexeBytes.get(id);
    if (bytes) { const d = await PDFDocument.load(bytes); inlinePageCounts.set(id, d.getPageCount()); }
    else inlinePageCounts.set(id, 0);
  }

  // Compter les pages des annexes orphelines
  let orphanPageCount = 0;
  for (const a of orphanAnnexes) {
    const bytes = annexeBytes.get(a.id);
    if (bytes) { const d = await PDFDocument.load(bytes); orphanPageCount += d.getPageCount(); }
  }

  const totalPages =
    segPageCounts.reduce((s, n) => s + n, 0) +
    [...inlinePageCounts.values()].reduce((s, n) => s + n, 0) +
    orphanPageCount;

  // --- Pass 2 : re-rendre chaque segment avec le bon pageOffset, puis concaténer ---
  // On track les pages des annexes pour leur appliquer un overlay header/footer
  // (les pages des segments ont déjà leur header/footer natif pdfmake).
  const merged = await PDFDocument.create();
  const annexePageIndices: number[] = [];
  // Première page de chaque annexe inline + son titre de section (pour le bandeau)
  const annexeTitlePages: Array<{ pageIndex: number; num: number; title: string }> = [];
  let pageOffset = 0;

  for (let i = 0; i < contentSegments.length; i++) {
    // Pages du segment pdfmake
    if (contentSegments[i].length > 0) {
      const blob = await renderSeg(contentSegments[i], pageOffset, totalPages);
      const segDoc = await PDFDocument.load(await blob.arrayBuffer());
      const pages = await merged.copyPages(segDoc, segDoc.getPageIndices());
      pages.forEach((p) => merged.addPage(p));
      pageOffset += segPageCounts[i];
    }

    // Annexe inline qui suit ce segment (si applicable)
    const annexeId = segmentAnnexeIds[i];
    if (annexeId) {
      const bytes = annexeBytes.get(annexeId);
      if (bytes) {
        const annexeDoc = await PDFDocument.load(bytes, { ignoreEncryption: true });
        const meta = annexeTitles.get(annexeId);
        const pageIndices = annexeDoc.getPageIndices();

        if (meta && pageIndices.length > 0) {
          // Première page : rétrécie et posée SOUS le bandeau de titre, pour
          // que le bandeau ne recouvre jamais le contenu original de l'annexe
          // (en-tête labo, logo...). Les pages suivantes restent pleine taille.
          const srcFirst = annexeDoc.getPage(0);
          const embedded = await merged.embedPage(srcFirst);
          const { width: w, height: h } = srcFirst.getSize();
          // Zone réservée en haut : header overlay + bandeau (cf. overlayAnnexeTitles)
          const reservedTop = 92; // bandeau jusqu'à h-80 + 12pt de respiration
          const marginBottom = 36;
          const scale = Math.min(1, (h - reservedTop - marginBottom) / h);
          annexeTitlePages.push({ pageIndex: merged.getPageCount(), ...meta });
          annexePageIndices.push(merged.getPageCount());
          const firstPage = merged.addPage([w, h]);
          firstPage.drawPage(embedded, {
            x: (w - w * scale) / 2,
            y: marginBottom,
            width: w * scale,
            height: h * scale,
          });

          const rest = await merged.copyPages(annexeDoc, pageIndices.slice(1));
          rest.forEach((p) => {
            annexePageIndices.push(merged.getPageCount());
            merged.addPage(p);
          });
        } else {
          const pages = await merged.copyPages(annexeDoc, pageIndices);
          pages.forEach((p) => {
            annexePageIndices.push(merged.getPageCount());
            merged.addPage(p);
          });
        }
        pageOffset += inlinePageCounts.get(annexeId) ?? 0;
      }
    }
  }

  // Annexes orphelines en fin de document
  for (const annexe of orphanAnnexes) {
    const bytes = annexeBytes.get(annexe.id);
    if (!bytes) continue;
    try {
      const annexeDoc = await PDFDocument.load(bytes, { ignoreEncryption: true });
      const pages = await merged.copyPages(annexeDoc, annexeDoc.getPageIndices());
      pages.forEach((p) => {
        annexePageIndices.push(merged.getPageCount());
        merged.addPage(p);
      });
    } catch (e) {
      console.error(`[PDF] Erreur annexe orpheline "${annexe.fileName}":`, e);
    }
  }

  // --- Overlay header (Affaire N + site + logo) et footer (X / total) sur les pages d'annexes ---
  if (annexePageIndices.length > 0) {
    await overlayHeaderFooter(merged, annexePageIndices, rapport, site, logoDataUrl);
  }

  // --- Bandeau de titre de section (même style que buildSectionTitle) sur la
  // première page de chaque annexe inline ---
  if (annexeTitlePages.length > 0) {
    await overlayAnnexeTitles(merged, annexeTitlePages);
  }

  const mergedBytes = await merged.save();
  return new Blob([mergedBytes as unknown as ArrayBuffer], { type: "application/pdf" });
}

/**
 * Dessine sur chaque page indiquee un header (Affaire N + site + logo) et un
 * footer (numero / total) coherents avec ceux generes par pdfmake sur les
 * segments. Utilise pdf-lib + fonts Helvetica standard (pas besoin d'embed
 * Roboto pour un texte court).
 *
 * Couleurs (cf. PDF_COLORS) :
 *   blue       #0000CC -> rgb(0, 0, 0.8)
 *   textMuted  #666666 -> rgb(0.4, 0.4, 0.4)
 *   textLight  #999999 -> rgb(0.6, 0.6, 0.6)
 */
async function overlayHeaderFooter(
  merged: import("pdf-lib").PDFDocument,
  pageIndices: number[],
  rapport: OfflineRapport,
  site: import("@/core/offline/db").OfflineSite,
  logoDataUrl: string | undefined
): Promise<void> {
  const pdfLib = await import("pdf-lib");
  const { StandardFonts, rgb } = pdfLib;

  const helvBold = await merged.embedFont(StandardFonts.HelveticaBold);
  const helv = await merged.embedFont(StandardFonts.Helvetica);

  // Embed logo PNG si dispo
  let logo: import("pdf-lib").PDFImage | null = null;
  let logoH = 0;
  let logoW = 0;
  if (logoDataUrl) {
    try {
      const base64 = logoDataUrl.split(",")[1];
      const bin = atob(base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      logo = await merged.embedPng(bytes);
      logoH = 20;
      logoW = (logo.width / logo.height) * logoH;
    } catch (e) {
      console.warn("[PDF] Embed logo annexes impossible :", e);
    }
  }

  const totalP = merged.getPageCount();
  const fontSize = PDF_FONT_SIZES.headerFooter; // 8
  const affaire = `Affaire ${rapport.numero ?? ""}`;
  const subt = ` \u2014 ${site.name}`; // U+2014 pour matcher la mise en forme pdfmake
  const blue = rgb(0, 0, 0.8);
  const muted = rgb(0.4, 0.4, 0.4);
  const light = rgb(0.6, 0.6, 0.6);

  for (const idx of pageIndices) {
    if (idx >= totalP) continue;
    const page = merged.getPage(idx);
    const { width: w, height: h } = page.getSize();
    const effPage = idx + 1;
    if (effPage === 1) continue; // page 1 = couverture (pas concernee en pratique)

    // Header : "Affaire N" bold blue + " - site" gris, top-left
    // pdfmake margin top=20 + descente font ; on cale a y = h - 32 (visuellement proche)
    const headerY = h - 32;
    page.drawText(affaire, { x: 40, y: headerY, size: fontSize, font: helvBold, color: blue });
    const aW = helvBold.widthOfTextAtSize(affaire, fontSize);
    page.drawText(subt, { x: 40 + aW, y: headerY, size: fontSize, font: helv, color: muted });

    // Logo top-right
    if (logo) {
      page.drawImage(logo, {
        x: w - 40 - logoW,
        y: h - 26 - logoH,
        width: logoW,
        height: logoH,
      });
    }

    // Footer : "X / total" bottom-right
    const footer = `${effPage} / ${totalP}`;
    const fW = helv.widthOfTextAtSize(footer, fontSize);
    page.drawText(footer, {
      x: w - 40 - fW,
      y: 30,
      size: fontSize,
      font: helv,
      color: light,
    });
  }
}

/**
 * Dessine sur la premiere page de chaque annexe un bandeau de titre de section
 * dans le style de buildSectionTitle (fond gris, filets, texte bleu bold
 * majuscules "N - TITRE"), sous le header. Le titre vient de la section custom
 * pdf-annexe (editable par l'utilisateur dans l'editeur de rapport).
 * Fonts standard pdf-lib (Helvetica) : caracteres hors WinAnsi remplaces par
 * leur version sans accent en secours.
 */
async function overlayAnnexeTitles(
  merged: import("pdf-lib").PDFDocument,
  titlePages: Array<{ pageIndex: number; num: number; title: string }>
): Promise<void> {
  const pdfLib = await import("pdf-lib");
  const { StandardFonts, rgb } = pdfLib;
  const helvBold = await merged.embedFont(StandardFonts.HelveticaBold);

  const blue = rgb(0, 0, 0.8);          // PDF_COLORS.blue #0000CC
  const grayBg = rgb(0.91, 0.91, 0.91); // PDF_COLORS.grayBg #E8E8E8
  const grayBorder = rgb(0.8, 0.8, 0.8); // PDF_COLORS.grayBorder #CCCCCC
  const fontSize = PDF_FONT_SIZES.heading1; // 14
  const bandH = 28;

  const totalP = merged.getPageCount();
  for (const { pageIndex, num, title } of titlePages) {
    if (pageIndex >= totalP) continue;
    const page = merged.getPage(pageIndex);
    const { width: w, height: h } = page.getSize();
    // Sous le header overlay (texte a h-32, logo jusqu'a h-46)
    const bandTop = h - 52;
    const bandBottom = bandTop - bandH;

    page.drawRectangle({
      x: 40, y: bandBottom, width: w - 80, height: bandH, color: grayBg,
    });
    page.drawLine({ start: { x: 40, y: bandTop }, end: { x: w - 40, y: bandTop }, thickness: 0.5, color: grayBorder });
    page.drawLine({ start: { x: 40, y: bandBottom }, end: { x: w - 40, y: bandBottom }, thickness: 0.5, color: grayBorder });

    // Separateur \u2013 (tiret demi-cadratin) : identique a buildSectionTitle
    const label = `${num} \u2013 ${title.toUpperCase()}`;
    const textOpts = { x: 48, y: bandBottom + 9, size: fontSize, font: helvBold, color: blue };
    try {
      page.drawText(label, textOpts);
    } catch {
      // Caractere hors WinAnsi (rare) : retomber sur une version sans accents
      const ascii = label.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^\x20-\x7E]/g, "-");
      page.drawText(ascii, textOpts);
    }
  }
}

/**
 * Formate une date de campagne en label court "Mois Annee" pour l'en-tete de
 * la colonne comparative du tableau de synthese. Ex: "Nov. 2025".
 */
function formatCampagneShortLabel(d: Date): string {
  const mois = ["Jan.", "Fév.", "Mars", "Avr.", "Mai", "Juin", "Juil.", "Août", "Sept.", "Oct.", "Nov.", "Déc."];
  return `${mois[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * Deep clone récursif qui préserve les fonctions PAR RÉFÉRENCE.
 * Nécessaire pour cloner les TDocumentDefinition de pdfmake : leur content
 * peut contenir des fonctions (largeurs de cellules, layouts custom) que
 * structuredClone() rejette ("could not be cloned"). On copie donc tout
 * profondément SAUF les fonctions, qui sont laissées telles quelles,
 * pdfmake ne mute pas les fonctions, juste les dataURL d'images.
 */
function deepCloneKeepFns<T>(v: T): T {
  if (v === null || typeof v !== "object") return v;
  if (v instanceof Blob || v instanceof ArrayBuffer) return v;
  if (Array.isArray(v)) return v.map(deepCloneKeepFns) as unknown as T;
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(v as Record<string, unknown>)) {
    const child = (v as Record<string, unknown>)[k];
    out[k] = typeof child === "function" ? child : deepCloneKeepFns(child);
  }
  return out as T;
}

// ─── Helpers ─────────────────────────────────────────────────

/** Charge une image depuis un chemin public et retourne un data URL */
async function loadImageAsDataUrl(path: string): Promise<string | undefined> {
  try {
    const response = await fetch(path);
    if (!response.ok) return undefined;
    const blob = await response.blob();
    return await blobToDataUrl(blob);
  } catch {
    return undefined;
  }
}

/** Détecte si une image encodée en dataUrl est portrait (hauteur > largeur).
 *  En cas d'échec de chargement, retourne false (on assume paysage par défaut). */
function detectPortrait(dataUrl: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    const timer = setTimeout(() => resolve(false), 5000);
    img.onload = () => {
      clearTimeout(timer);
      resolve(img.naturalHeight > img.naturalWidth);
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(false);
    };
    img.src = dataUrl;
  });
}

/** Convertit un Blob en data URL (base64) avec timeout de 10s */
function blobToDataUrl(blob: Blob): Promise<string> {
  if (!blob || !(blob instanceof Blob) || blob.size === 0) {
    return Promise.reject(new Error("Blob invalide ou vide"));
  }
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("Timeout lecture blob")), 10000);
    const reader = new FileReader();
    reader.onload = () => { clearTimeout(timer); resolve(reader.result as string); };
    reader.onerror = () => { clearTimeout(timer); reject(new Error("Erreur lecture blob")); };
    reader.readAsDataURL(blob);
  });
}
